import type { Change, CommitTransactionConfig, Transaction } from "../types";
import { set as lodashSet, unset as lodashUnset } from "lodash-es";
import { createTransaction } from "../utils";

export const DEFAULT_CONTEXTS: readonly string[] = ["global"];

export const initState = <
  T extends Record<string, unknown>,
  C extends readonly string[]
>(
  initialState: T,
  contexts: C = DEFAULT_CONTEXTS as C,
  defaultContext: C[number] = DEFAULT_CONTEXTS[0]
) => {
  if (
    typeof initialState !== "object" ||
    initialState === null ||
    Array.isArray(initialState)
  ) {
    throw new Error("Invalid initial state. Expected an object");
  }

  type ContextType = C[number];

  const undoStacks: Record<ContextType, Transaction[]> = contexts.reduce(
    (acc, context) => {
      acc[context as ContextType] = [];
      return acc;
    },
    {} as Record<ContextType, Transaction[]>
  );

  const redoStacks: Record<ContextType, Transaction[]> = contexts.reduce(
    (acc, context) => {
      acc[context as ContextType] = [];
      return acc;
    },
    {} as Record<ContextType, Transaction[]>
  );

  let currentContext: ContextType = defaultContext;

  // Get current context's stacks
  const getCurrentStacks = () => ({
    undoStack: undoStacks[currentContext] || [],
    redoStack: redoStacks[currentContext] || []
  });

  const setActiveUndoRedoContext = (newContext: ContextType) => {
    // Clear previous context's stacks if it's not
    if (currentContext !== defaultContext) {
      undoStacks[currentContext] = [];
      redoStacks[currentContext] = [];
    }

    // Initialize new context's stacks if they don't exist
    if (!undoStacks[newContext]) {
      undoStacks[newContext] = [];
    }
    if (!redoStacks[newContext]) {
      redoStacks[newContext] = [];
    }

    currentContext = newContext;
  };

  // ledger will be used to track unsaved changes
  // whatever is managing the save will pull transactions from
  // ledger and create a save payload from it.
  // If the save succeeds, we'll remove the transaction IDs that
  // got saved.
  // If the save fails, we can retry the save.
  const ledger: Transaction[] = [];

  /**
   * Reverts changes up to a specified commit or all changes if commitId is null.
   * Note: this doesn't remove the commits from the undo stack, but adds a new commit
   * @param commitId - The commitId to revert until, or null to revert all changes in the undo stack
   */
  const revertToParticularCommit = (
    commitId: string | null,
    txMetadata?: CommitTransactionConfig["transactionMetadata"]
  ) => {
    const { undoStack } = getCurrentStacks();

    // If commitId is null, then setting to -1 to revert all changes (we'll revert slice undo stack from 0th index)
    const indexOfParticularCommit =
      commitId === null ? -1 : undoStack.findIndex(tx => tx.id === commitId);

    if (commitId !== null && indexOfParticularCommit === -1) {
      throw new Error("Commit not found in ledger");
    }

    const commits = undoStack
      .slice(indexOfParticularCommit + 1)
      .reverse()
      .flatMap(tx => tx.changes);

    const newCommit = createTransaction(commits, txMetadata);

    // Commit all the changes as a single commit, so that they are logged in undo stack
    commitTransaction(newCommit);
  };

  const undo = (
    txMetadata?: CommitTransactionConfig["transactionMetadata"]
  ) => {
    const { undoStack, redoStack } = getCurrentStacks();
    const lastTransaction = undoStack.pop();
    if (lastTransaction) {
      redoStack.push(reverseTransaction(lastTransaction));

      // We skip the undo stack because we don't want to empty the
      // redo stack because we're manually pushing the reverse of the
      // transaction to the redo stack.
      commitTransaction(
        { ...lastTransaction, transactionMetadata: txMetadata },
        {
          shouldSkipUndoStack: true
        }
      );
    }
  };

  const redo = (
    txMetadata?: CommitTransactionConfig["transactionMetadata"]
  ) => {
    const { undoStack, redoStack } = getCurrentStacks();
    const lastTransaction = redoStack.pop();

    if (lastTransaction) {
      undoStack.push(reverseTransaction(lastTransaction));

      // We skip the undo stack because we don't want to empty the
      // redo stack. Also, we're manually pushing the reverse of the
      // transaction to the undo stack, so we don't want to push it
      // again.
      commitTransaction(
        { ...lastTransaction, transactionMetadata: txMetadata },
        {
          shouldSkipUndoStack: true
        }
      );
    }
  };

  /**
   * When things get committed to state, we put the changes into a transaction
   * but in the reversed form. This function helps us to get that reversed
   * transaction.
   * @param transaction
   * @returns
   */
  const reverseTransaction = (transaction: Transaction): Transaction => {
    const reversedChanges = transaction.changes.map(change => ({
      ...change,
      from: change.to,
      to: change.from
    })) as Change[];
    reversedChanges.reverse();

    return {
      id: transaction.id,
      changes: reversedChanges
    };
  };

  /**
   * Makes changes to the actual state, by going through transaction and
   * committing individual changes to the state.
   * Also pushes the reverse of the transaction to the undoStack.
   * @param transaction
   */
  const commitTransaction = (
    transaction: Transaction,
    config: CommitTransactionConfig = {}
  ) => {
    const { shouldSkipUndoStack = false, shouldSkipLedger = false } = config;

    transaction.changes.forEach(change => {
      // Check if the change is a delete operation, which is determined
      // if `to` is undefined

      if (change.to === undefined) {
        lodashUnset(state, change.path);
        return;
      }

      // Otherwise, just set the value
      lodashSet(state, change.path, change.to);
    });
    if (!shouldSkipLedger) {
      // Push the transaction to the ledger
      ledger.push(transaction);
    }

    if (!shouldSkipUndoStack) {
      const { undoStack, redoStack } = getCurrentStacks();
      // Clear the redo stack
      redoStack.splice(0, redoStack.length);

      // Push the reverse transaction to the undo stack
      undoStack.push(reverseTransaction(transaction));
    }
  };

  /**
   * Wrapper around commitTransaction that creates a transaction from the
   * given changes.
   * @param changes
   */
  const commit = (changes: Change[], config: CommitTransactionConfig = {}) => {
    const transaction = createTransaction(changes, config.transactionMetadata);
    commitTransaction(transaction, config);
    return transaction.id;
  };

  // Load the process
  const state = initialState;

  // Removes the transaction upto (and including) the given ID from the ledger
  const removeFromLedger = (lastLedgerId: string) => {
    const index = ledger.findIndex(tx => tx.id === lastLedgerId);
    ledger.splice(0, index + 1);
  };

  // Clears undo redo stacks
  const clearUndoStack = () => {
    const { undoStack } = getCurrentStacks();
    undoStack.length = 0;
  };
  const clearRedoStack = () => {
    const { redoStack } = getCurrentStacks();
    redoStack.length = 0;
  };

  const clearLedger = () => {
    // empty the ledger array
    ledger.length = 0;
  };

  return {
    state,
    commit,
    undo,
    redo,
    ledger,
    removeFromLedger,
    clearUndoStack,
    clearRedoStack,
    clearLedger,
    revertToParticularCommit,
    setActiveUndoRedoContext,
    meta: {
      getUndoLength: () => {
        const { undoStack } = getCurrentStacks();
        return undoStack.length;
      },
      getRedoLength: () => {
        const { redoStack } = getCurrentStacks();
        return redoStack.length;
      },
      getLedgerLength: () => {
        return ledger.length;
      },
      getPreviousCommitId: () => {
        const { undoStack } = getCurrentStacks();
        return undoStack[undoStack.length - 1]?.id;
      }
    }
  };
};
